import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  ProgressBar,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { createAutomationApp, validateAppName } from '@/services/projectService';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuth } from '@/auth';

const useStyles = makeStyles({
  statusContainer: {
    marginTop: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  statusText: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  errorContainer: {
    marginTop: tokens.spacingVerticalS,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export type CreateAppDialogProps = {
  open: boolean;
  onClose: () => void;
  existingAppNames: string[];
  onAppCreated?: () => void;
};

export const CreateAppDialog = ({
  open,
  onClose,
  existingAppNames,
  onAppCreated,
}: CreateAppDialogProps) => {
  const styles = useStyles();
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedLocation } = useAppStore();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const projectName = useMemo(() => selectedProject ?? '', [selectedProject]);
  // TODO: temporary location handling until we have a proper location selection flow - default to brazilsouth for now since it's the only region available
  const location = useMemo(() => selectedLocation === 'brazilus' ? 'brazilsouth' : (selectedLocation ?? 'brazilsouth'), [selectedLocation]);
  const [newAppName, setNewAppName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [provisioningState, setProvisioningState] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const appCreationStartedRef = useRef(false);

  // Validate app name using shared validation logic
  const appNameValidation = useMemo(() => {
    return validateAppName(newAppName, existingAppNames);
  }, [newAppName, existingAppNames]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setNewAppName('');
      setProvisioningState(null);
      setErrorMessage(null);
      setIsCreating(false);
      appCreationStartedRef.current = false;
    }
  }, [open]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    // Clear polling interval if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    // Only refresh apps list if user actually initiated app creation (e.g.,
    // closed dialog while provisioning was in progress). Skip refresh when
    // dialog is dismissed without creating anything.
    if (appCreationStartedRef.current) {
      onAppCreated?.();
    }
    setNewAppName('');
    setProvisioningState(null);
    setErrorMessage(null);
    setIsCreating(false);
    onClose();
  }, [onClose, onAppCreated]);

  const pollProvisioningStatus = useCallback(async (url: string) => {
    try {
      const token = await getOttoToken();
      if (!token) return;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const state = result.status || 'Unknown';
        const provState = state.toLowerCase() === 'pending' ? 'Provisioning...' : state;
        setProvisioningState(provState);

        // Stop polling if terminal state reached
        if (state === 'Succeeded' || state === 'Failed' || state === 'Canceled') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          setIsCreating(false);
          onAppCreated?.();
          
          // Auto-close on success only
          if (state === 'Succeeded') {
            addNotification({
              type: 'success',
              title: 'Application created',
              message: `Application "${newAppName.trim()}" has been created successfully.`,
            });
            onClose();
          } else {
            addNotification({
              type: 'error',
              title: 'Application creation failed',
              message: `Application provisioning ${state.toLowerCase()}.`,
            });
            setErrorMessage(`Application provisioning ${state.toLowerCase()}.`);
          }
        }
      }
    } catch (error) {
      console.error('Error polling status:', error);
    }
  }, [getOttoToken, onAppCreated, onClose, addNotification, newAppName]);

  const handleCreateApp = useCallback(async () => {
    if (!appNameValidation.isValid) return;
    setIsCreating(true);
    setProvisioningState('Creating...');
    setErrorMessage(null);
    
    const trimmedAppName = newAppName.trim();
    
    try {
      const result = await createAutomationApp(projectName, trimmedAppName, location, getOttoToken);
      appCreationStartedRef.current = true;

      if (result?.statusUrl) {
        setProvisioningState('Provisioning...');
        
        // Start polling every 10 seconds
        pollingIntervalRef.current = setInterval(() => {
          void pollProvisioningStatus(result.statusUrl!);
        }, 10000);
        
        // Also poll immediately
        void pollProvisioningStatus(result.statusUrl);
      } else {
        // No status URL, assume success
        setProvisioningState('Succeeded');
        setIsCreating(false);
        onAppCreated?.();
      }
    } catch (error) {
      console.error('Error creating app:', error);
      const message = error instanceof Error ? error.message : 'Failed to create application';
      addNotification({
        type: 'error',
        title: 'Application creation failed',
        message,
      });
      setErrorMessage(message);
      setProvisioningState('Failed');
      setIsCreating(false);
    }
  }, [projectName, location, getOttoToken, newAppName, appNameValidation.isValid, pollProvisioningStatus, onAppCreated, addNotification]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Create application</DialogTitle>
          <DialogContent>
            <Field 
              label="Application name" 
              required
              validationState={!isCreating && appNameValidation.error ? 'error' : 'none'}
              {...(!isCreating && appNameValidation.error ? { validationMessage: appNameValidation.error } : {})}
            >
              <Input
                value={newAppName}
                onChange={(_, data) => setNewAppName(data.value)}
                placeholder="Enter application name"
                disabled={isCreating}
              />
            </Field>
            {provisioningState && (
              <div className={styles.statusContainer}>
                {isCreating && <ProgressBar />}
                <Text className={styles.statusText}>Status: {provisioningState}</Text>
              </div>
            )}
            {errorMessage && (
              <div className={styles.errorContainer}>
                <Text className={styles.errorText}>{errorMessage}</Text>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              {isCreating ? 'Close' : 'Cancel'}
            </Button>
            <Button
              appearance="primary"
              onClick={handleCreateApp}
              disabled={!appNameValidation.isValid || isCreating}
            >
              Create
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
